<template>
  <IdentityShell top-link="sign-in">
    <div class="signup-org-page">
      <!-- Step 2: the social path completed "your account" via OAuth on
           SignupPage (step 1); step 3 is the onboarding wizard itself. -->
      <IdentitySignupProgress
        :label="
          $t(
            'auth.identity.signupOrganization.yourOrganization',
            'Your organization'
          )
        "
        :step="2"
        :total="3"
      />

      <h1 class="section-headline signup-org-page__title">
        {{
          $t(
            'auth.identity.signupOrganization.tellUsAboutYour',
            'Tell us about your'
          )
        }}
        <span class="highlight">{{
          $t(
            'auth.identity.signupOrganization.organizationHighlight',
            'organization'
          )
        }}</span
        >{{ $t('auth.identity.signupOrganization.organizationTrail', '.') }}
      </h1>

      <AlertDanger v-if="firstError" show class="mb-3">
        {{ firstError }}
      </AlertDanger>

      <FormBase
        class="auth-form signup-org-page__form"
        @submit.prevent="onSubmit"
      >
        <FormGroupBase
          class="mb-3"
          :label="
            $t(
              'auth.identity.signupOrganization.organizationNameLabel',
              'Organization name'
            )
          "
          :state="fieldState('organization_name')"
          :invalid-feedback="fieldError('organization_name')"
        >
          <InputText
            v-model="organizationName"
            autofocus
            autocomplete="organization"
            :placeholder="
              $t(
                'auth.identity.signupOrganization.organizationNamePlaceholder',
                'Capilano Recreation'
              )
            "
            :state="fieldState('organization_name')"
          />
        </FormGroupBase>

        <FormGroupBase
          class="mb-3"
          :label="
            $t(
              'auth.identity.signupOrganization.organizationWebsiteLabel',
              'Organization website'
            )
          "
          :state="fieldState('website')"
          :invalid-feedback="fieldError('website')"
        >
          <InputUrl
            v-model="website"
            autocomplete="url"
            inputmode="url"
            :placeholder="
              $t(
                'auth.identity.signupOrganization.organizationWebsitePlaceholder',
                'https://example.org'
              )
            "
            :state="fieldState('website')"
          />
        </FormGroupBase>

        <button
          type="submit"
          class="btn-communal btn-communal--block"
          :disabled="submitting"
        >
          <span v-if="submitting">
            {{
              $t(
                'auth.identity.signupOrganization.settingEverythingUp',
                'Setting everything up…'
              )
            }}
          </span>
          <span v-else>
            {{ submitLabel }}
            <span class="btn-communal__arrow" aria-hidden="true">→</span>
          </span>
        </button>
        <p class="signup-org-page__trust">
          {{
            $t(
              'auth.identity.signupOrganization.readyInUnderAMinute',
              "We'll have everything ready in under a minute."
            )
          }}
        </p>
      </FormBase>

      <p class="auth-foot-link">
        {{
          $t(
            'auth.identity.signupOrganization.alreadyHaveAccount',
            'Already have an account?'
          )
        }}
        <RouterLink to="/login">
          {{
            $t(
              'auth.identity.signupOrganization.signInInstead',
              'Sign in instead'
            )
          }}
        </RouterLink>
      </p>
    </div>

    <template #welcome>
      <div class="signup-org-welcome">
        <p class="kicker">
          {{ $t('auth.identity.signupOrganization.whatsNext', "What's next") }}
        </p>

        <p class="signup-org-welcome__copy">
          <template v-if="pendingEmail">
            {{
              $t('auth.identity.signupOrganization.signedInAs', 'Signed in as')
            }}
            <strong>{{ pendingEmail }}</strong
            >{{
              $t(
                'auth.identity.signupOrganization.signedInAsOutro',
                ". Name your organization and we'll have everything ready in a minute."
              )
            }}
          </template>
          <template v-else>
            {{
              $t(
                'auth.identity.signupOrganization.nameYourOrganization',
                "Name your organization and we'll have everything ready in a minute. Here's what happens after you submit:"
              )
            }}
          </template>
        </p>

        <ol class="next-list">
          <li class="next-list__item">
            <span class="next-list__num tabular-nums">{{
              $t('auth.identity.signupOrganization.stepOneNumber', '01')
            }}</span>
            <div class="next-list__body">
              <strong>{{
                $t(
                  'auth.identity.signupOrganization.pickWhatYouRun',
                  'Pick what you run'
                )
              }}</strong>
              <span>
                {{
                  $t(
                    'auth.identity.signupOrganization.pickWhatYouRunBody',
                    'Memberships, programs, facilities, volunteering, donations. Choose the areas that fit your organization.'
                  )
                }}
              </span>
            </div>
          </li>
          <li class="next-list__item">
            <span class="next-list__num tabular-nums">{{
              $t('auth.identity.signupOrganization.stepTwoNumber', '02')
            }}</span>
            <div class="next-list__body">
              <strong>{{
                $t(
                  'auth.identity.signupOrganization.weBuildItForYou',
                  'We build it for you'
                )
              }}</strong>
              <span>
                {{
                  $t(
                    'auth.identity.signupOrganization.weBuildItForYouBody',
                    'Share your website and our AI drafts your real facilities, programs, and memberships, or start from ready-made sample content.'
                  )
                }}
              </span>
            </div>
          </li>
          <li class="next-list__item">
            <span class="next-list__num tabular-nums">{{
              $t('auth.identity.signupOrganization.stepThreeNumber', '03')
            }}</span>
            <div class="next-list__body">
              <strong>{{
                $t(
                  'auth.identity.signupOrganization.aHumanChecksIn',
                  'A human checks in'
                )
              }}</strong>
              <span>
                {{
                  $t(
                    'auth.identity.signupOrganization.aHumanChecksInBody',
                    "A real person from our team reaches out to make sure everything looks right. There's no scripted sales pitch."
                  )
                }}
              </span>
            </div>
          </li>
        </ol>
      </div>
    </template>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import {
  IdentityShell,
  IdentitySignupProgress,
} from '@/components/feature/Identity';
import { AlertDanger } from '@/components/ui/Alert';
import { InputText, InputUrl } from '@/components/ui/Form';
import { useIdentityForm } from '@/composables/useIdentityForm';
import {
  TRIAL_ORGANIZATION_FAILED,
  TRIAL_ORGANIZATION_SUBMITTED,
  TRIAL_ORGANIZATION_VIEWED,
} from '@/lib/constants/funnelEvents';
import { trackEvent } from '@/lib/services/analytics/Measurement';
import { detectBrowserTimezone } from '@/lib/services/utils/BrowserTimezone';

const { t } = useI18n({ useScope: 'global' });

const pendingEmail = computed(() => window.boot?.pending?.email ?? '');

const organizationName = ref('');
const website = ref('');

// The CTA adopts the org's own name as they type it. Long names fall back
// so the block-level button doesn't wrap.
const submitLabel = computed(() => {
  const name = organizationName.value.trim();
  return name.length > 0 && name.length <= 28
    ? t(
        'auth.identity.signupOrganization.setUpNamed',
        { name },
        'Set up {name}'
      )
    : t(
        'auth.identity.signupOrganization.setUpMyOrganization',
        'Set up my organization'
      );
});

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/signup/organization' });

// The social path lands here without ever hitting the email form, so this is
// the first funnel event those signups produce.
onMounted(() => {
  trackEvent(TRIAL_ORGANIZATION_VIEWED, {
    signup_method: pendingEmail.value ? 'social' : 'email',
  });
});

async function onSubmit() {
  trackEvent(TRIAL_ORGANIZATION_SUBMITTED);

  const result = await submit({
    organization_name: organizationName.value,
    website: website.value,
    timezone: detectBrowserTimezone(),
  });

  if (!result.ok) {
    trackEvent(TRIAL_ORGANIZATION_FAILED, { reason: result.failure });
  }
}
</script>

<style lang="scss" scoped>
// Rendered inside IdentityShell's .communal-surface scope — raw <h1>, raw
// submit <button>, .section-headline, .next-list, and .btn-communal are
// deliberate marketing-surface classes, not in-product wrappers. See
// IdentityShell.vue for the full contract.

.signup-org-page__title {
  margin-bottom: 1.25rem;
}

.signup-org-page__trust {
  margin: 0.75rem 0 0;
  text-align: center;
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--ink-500);
}

.signup-org-welcome__copy {
  margin: 0;
  max-width: 34ch;
  font-size: 1rem;
  line-height: 1.55;
  color: var(--ink-600);

  strong {
    color: var(--ink-900);
    font-weight: 600;
  }
}
</style>
